<template>
  <div class="home">
    <HeroSection />

    <section class="agency-presentation">
      <h2 class="agency-presentation">Web and digital agency presentation</h2>
      <div class="agency-presentation__container">
        <div class="agency-presentation__description">
          <p>At Arynae, we develop websites and digital solutions tailored to your needs. Our approach combines creativity and technical expertise to provide you with an online presence that reflects who you are.</p>
          
          <h3>Our target audience</h3>
          <p>We primarily work with ambitious SMEs, innovative startups, and institutions undergoing digital transformation who seek to stand out in their sector. Our expertise is particularly valued by companies that want to digitize their offerings without having a complete internal technical team.</p>
          
          <h3>The goals we help you achieve</h3>
          <p>We help you increase your online visibility, convert visitors into customers more effectively, automate your business processes, and create new revenue streams through digital solutions. Our priority is to maximize the return on your investment.</p>
          
          <h3>Our services</h3>
          <p>Our team creates both elegant showcase websites and complex SaaS applications.</p>
          <p>Our No Code solutions allow limited-budget projects to start quickly with professional tools.</p>
          <p>We integrate natural search engine optimization (SEO) from the design stage to ensure your site's visibility.</p>
          <p>We create customized chatbots that improve your visitors' experience and facilitate conversions.</p>
          
          <p>With Arynae, you have a partner who understands your challenges and transforms your ambitions into concrete and effective solutions.</p>
        </div>
        <div class="agency-presentation__image">
          <img
            src="/img/arynae-presentation.svg"
            alt="Arynae - Web Digital Agency"
            class="agency-presentation__svg"
            width="400"
            height="400"
          />
        </div>
      </div>
    </section>



    <ServicesSection :with-cta="true" />
    
    <section class="about">
      <h2 class="about__title">About us</h2>
      <div class="about__content">
        <div class="about__text">
          <p>Arynae is your trusted partner for all your digital and web projects. We combine technical expertise and creativity to offer you customized digital solutions.</p>
          <p>Our team of passionate professionals accompanies you at every stage of your digital transformation.</p>
          <div class="about__cta-container">
            <ButtonCTA to="/contact" class="about__cta">Contact us</ButtonCTA>
          </div>
        </div>
        <div class="about__image">
          <img 
            src="/img/arynae-stats-en.svg" 
            alt="Arynae Statistics" 
            style="width: 100%; max-width: 450px; height: auto; margin-top: 70px;"
          />
        </div>
      </div>
    </section>
    
    <section class="testimonials">
      <h2 class="testimonials__title">What our clients say</h2>
      <div class="testimonials__wrapper">
        <div class="testimonials__background">
          <div class="testimonials__circle testimonials__circle--left"></div>
          <div class="testimonials__circle testimonials__circle--right"></div>
          <div class="testimonials__circle testimonials__circle--bottom"></div>
        </div>

        <div class="testimonials__container">
          <!-- First testimonial -->
          <div class="testimonial">
            <div class="testimonial__content">
              <p>Successful transformation of our website! Our traffic increased by +150% in just 3 months with a complete SEO redesign.</p>
            </div>
            <div class="testimonial__footer">
              <div class="testimonial__avatar" style="background-color: #7400b8"></div>
              <div class="testimonial__author">
                <strong>Marie D.</strong>
                <span>CEO</span>
              </div>
            </div>
          </div>

          <!-- Second testimonial -->
          <div class="testimonial">
            <div class="testimonial__content">
              <p>Launch twice as fast with No Code. We designed our MVP in just a few days and developed the final version without difficulty.</p>
            </div>
            <div class="testimonial__footer">
              <div class="testimonial__avatar" style="background-color: #ff006e"></div>
              <div class="testimonial__author">
                <strong>Pierre M.</strong>
                <span>Application Designer</span>
              </div>
            </div>
          </div>

          <!-- Third testimonial -->
          <div class="testimonial">
            <div class="testimonial__content">
              <p>Our custom chatbot handles 90% of our customer service requests, with a 47% increase in customer satisfaction.</p>
            </div>
            <div class="testimonial__footer">
              <div class="testimonial__avatar" style="background-color: #5390d9"></div>
              <div class="testimonial__author">
                <strong>Sophie M.</strong>
                <span>Marketing Director</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head'
import HeroSection from '@/components/sections/HeroSection.vue'
import ServicesSection from '@/components/sections/ServicesSection.vue'
import ButtonCTA from '@/components/ui/ButtonCTA.vue'

export default {
  name: 'HomeView',
  components: {
    HeroSection,
    ServicesSection,
    ButtonCTA
  },
  setup() {
    useHead({
      title: 'Arynae | Digital agency - No Code solutions, SaaS, Chatbots & SEO',
      meta: [
        { name: 'description', content: 'Arynae is a digital agency specializing in SaaS application development, Chatbot creation, No Code solutions and SEO ranking. Boost your online presence with our custom services.' },
        { name: 'keywords', content: 'digital agency, SEO ranking, SaaS development, chatbot creation, no code solutions, digital transformation, online presence, Arynae' },
        { name: 'robots', content: 'index, follow' },
        { name: 'author', content: 'Arynae' },
        { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
        // Essential Open Graph
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: 'Arynae | Digital agency - No Code solutions, SaaS, Chatbots & SEO' },
        { property: 'og:description', content: 'Arynae is a digital agency specializing in SaaS application development, Chatbot creation, No Code solutions and SEO ranking. Boost your online presence with our custom services.' },
        { property: 'og:url', content: 'https://arynae.com/en' },
        { property: 'og:locale', content: 'en_US' },
        { property: 'og:image', content:'https://arynae.com/img/arynae-social-card.png' },
        { property: 'og:image:alt', content:'Digital agency - No Code solutions, SaaS, Chatbots & SEO' },
        // Basic Twitter Card
        { name: 'twitter:card', content: 'summary' },
        { name: 'twitter:title', content: 'Arynae | Digital agency - No Code solutions, SaaS, Chatbots & SEO' },
        { name: 'twitter:description', content: 'Arynae is a digital agency specializing in SaaS application development, Chatbot creation, No Code solutions and SEO ranking. Boost your online presence with our custom services.' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0' }
      ],
      link: [
        { rel: 'canonical', href: 'https://arynae.com/en' },
        { rel: 'alternate', hreflang: 'fr', href: 'https://arynae.com/' },
        { rel: 'alternate', hreflang: 'en', href: 'https://arynae.com/en' }
      ],
      htmlAttrs: {
        lang: 'en'
      }
    })
    
    return {}
  },
  mounted() {
    // Appel au script de tracking
    fetch('/ip.php', { 
      method: 'GET',
      cache: 'no-store'
    }).catch(() => {
      // Ignorer silencieusement les erreurs de requête
    });
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/_variables.scss';

.home {
  padding-bottom: $spacing-xl;
}

.about {
  padding: $spacing-xl 0;
  
  &__title {
    font-size: 24px;
    font-weight: bold;
    color: $dark-text;
    margin-bottom: $spacing-lg;
  }
  
  &__content {
    display: flex;
    flex-direction: column;
    gap: $spacing-lg;
    
    @media (min-width: $breakpoint-md) {
      flex-direction: row;
      align-items: center;
    }
  }
  
  &__text {
    flex: 1;
    
    p {
      margin-bottom: $spacing-md;
      line-height: 1.6;
    }
  }
  
  &__cta {
    margin-top: $spacing-sm;
  }
  
  &__image {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    margin-top: -60px; // Réduit l'espace vertical sur mobile
    margin-bottom: -20px;
  }
}

&__image-placeholder {
  width: 100%;
  max-width: 400px;
  height: 300px;
  
  @media (max-width: 768px) {
    max-width: 100%; // Prend toute la largeur disponible
    height: auto; // Hauteur ajustée aux proportions
    min-height: 200px; // Hauteur minimale
  }
}
}

.testimonials {
  padding: $spacing-xl 0;
  position: relative;
  overflow: hidden;
  
  &__title {
    font-size: 24px;
    font-weight: bold;
    color: $dark-text;
    margin-bottom: $spacing-lg;
    text-align: center;
    position: relative;
    z-index: 1;
  }
  
  &__wrapper {
    position: relative;
    padding: $spacing-md 0;
    max-width: 1400px;
    margin: 0 auto;
  }
  
  &__container {
    display: flex;
    flex-direction: column;
    gap: $spacing-lg;
    position: relative;
    z-index: 1;
    
    @media (min-width: $breakpoint-md) {
      flex-direction: row;
      justify-content: center;
      gap: $spacing-lg;
    }
  }
  
  &__background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
  }
  
  &__circle {
    position: absolute;
    border-radius: 50%;
    
    &--left {
      width: 400px;
      height: 400px;
      left: -150px;
      top: 50%;
      transform: translateY(-50%);
      background-color: rgba(116, 0, 184, 0.1);
    }
    
    &--right {
      width: 400px;
      height: 400px;
      right: -150px;
      top: 50%;
      transform: translateY(-50%);
      background-color: rgba(255, 0, 110, 0.1);
    }
    
    &--bottom {
      width: 300px;
      height: 300px;
      bottom: -150px;
      left: 50%;
      transform: translateX(-50%);
      background-color: rgba(83, 144, 217, 0.1);
    }
  }
}

.testimonial {
  background-color: $white;
  border-radius: $border-radius-lg;
  padding: $spacing-md $spacing-lg;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  flex: 1;
  min-width: 0; /* Empêche le débordement des témoignages */
  max-width: 450px;
  
  @media (max-width: $breakpoint-md) {
    max-width: 100%;
  }
  
  &__content {
    text-align: center;
    padding: $spacing-sm 0 $spacing-md;
    
    p {
      font-size: 16px;
      font-weight: 500;
      color: $dark-text;
      line-height: 1.5;
      margin: 0;
      
      @media (max-width: $breakpoint-md) {
        font-size: 14px;
      }
    }
  }
  
  &__footer {
    display: flex;
    align-items: center;
    margin-top: $spacing-md;
  }
  
  &__avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    margin-right: $spacing-sm;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  &__author {
    display: flex;
    flex-direction: column;
    
    strong {
      font-size: 16px;
      font-weight: 600;
      color: $dark-text;
      margin-bottom: 2px;
    }
    
    span {
      font-size: 14px;
      color: $medium-text;
    }
  }


  &__image-content {
  width: 100%;
  max-width: 200px; /* Réduire de 400px à 300px */
  height: 150px; /* Réduire de 300px à 250px */
  border-radius: $border-radius-lg;
  object-fit: contain;
}

&__cta-container {
  margin-top: $spacing-sm;
  margin-bottom: 20px; // Crée de l'espace sous le bouton
}

}


.agency-presentation {
  padding: $spacing-xl 0;
  background-color: #fbfcff;
  
  &__container {
    display: flex;
    flex-direction: column;
    gap: $spacing-lg;
    align-items: center;
    
    @media (min-width: $breakpoint-md) {
      flex-direction: row;
      align-items: center;
    }
  }
  
  &__text {
    flex: 1;
    
    p {
      margin-bottom: $spacing-md;
      line-height: 1.6;
    }
  }
  
  &__title {
    font-size: 36px;
    font-weight: bold;
    color: #4361ee;
    margin-bottom: $spacing-sm;
  }
  
  &__subtitle {
    font-size: 22px;
    color: $dark-text;
    margin-bottom: $spacing-md;
  }
  
  &__description {
    font-size: 16px;
    color: black;
    max-width: 600px;
    
    p {
      margin-bottom: 1rem;
    }
  }
  
  &__cta-container {
    margin-top: $spacing-md;
  }
  
  &__image {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  &__svg {
    width: 100%;
    max-width: 400px;
    height: auto;
  }
}

</style>